import { useEffect, useRef } from 'react';
import { useGameStore } from '@/state/gameStore';

export const AFK_PANIC_IDLE_MS = 60_000;

/**
 * After 60s without a mousemove, SoulCaptcha fears the player has died and
 * awards a pity token. Gated on the persistent `afkPanicTriggered` flag
 * so it only ever happens once per save.
 */
export function useAfkPanic(enabled = true): void {
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const clear = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const fire = () => {
      const { flags, awardToken, setFlag } = useGameStore.getState();
      if (flags.afkPanicTriggered) return;
      awardToken('afkPanic');
      setFlag('afkPanicTriggered', true);
    };

    const arm = () => {
      clear();
      if (useGameStore.getState().flags.afkPanicTriggered) return;
      timeoutRef.current = window.setTimeout(() => {
        fire();
      }, AFK_PANIC_IDLE_MS);
    };

    const onMove = () => {
      arm();
    };

    arm();
    window.addEventListener('mousemove', onMove, { passive: true });

    return () => {
      clear();
      window.removeEventListener('mousemove', onMove);
    };
  }, [enabled]);
}
